import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Lock, Paperclip, Send, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRIVATE_PREFIX_RE = /^\/private\s/i;
const SIMPLE_MENTION_RE = /@[A-Za-z][A-Za-z0-9_]*/;

// Phase 7c — attachment limits (must match backend constants)
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const MAX_ATTACHMENTS_PER_MESSAGE = 8;

function findActiveMention(text, cursor) {
  if (cursor == null) return null;
  let i = cursor - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === "@") {
      if (i === 0 || /\s/.test(text[i - 1])) {
        const query = text.slice(i + 1, cursor);
        if (/^[A-Za-z0-9_]*$/.test(query)) {
          return { start: i, query };
        }
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

function newLocalId() {
  return `att-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function Composer({
  workspaceId,
  currentUser,
  otherUsers,
  sending,
  onSend,
  trialExhausted,
  prefillText,
  onPrefillConsumed,
}) {
  const [text, setText] = useState("");
  const [cursor, setCursor] = useState(0);
  const [pickerIndex, setPickerIndex] = useState(0);
  const [dismissedAt, setDismissedAt] = useState(null);
  // Phase 7c — attachment staging
  const [attachments, setAttachments] = useState([]); // {local_id, file, name, size, type, status, progress, error, server_id, preview_url}
  const [isDragging, setIsDragging] = useState(false);
  const dragCountRef = useRef(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const mention = useMemo(
    () => findActiveMention(text, cursor),
    [text, cursor]
  );

  const candidateUsers = useMemo(() => {
    return (otherUsers || []).filter((u) => u.id !== currentUser?.id);
  }, [otherUsers, currentUser?.id]);

  const filteredUsers = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    if (q === "") return candidateUsers;
    return candidateUsers.filter((u) =>
      (u.display_name || "").toLowerCase().startsWith(q)
    );
  }, [mention, candidateUsers]);

  const showPicker =
    mention !== null &&
    filteredUsers.length > 0 &&
    dismissedAt !== mention.start;

  useEffect(() => {
    setPickerIndex(0);
  }, [showPicker, mention?.start]);

  // Phase ChatPolish — empty-state prompt tiles set `prefillText` on the
  // composer. We mirror it into the textarea, focus + select-end, and let
  // the parent know we consumed it so future tile clicks re-trigger.
  useEffect(() => {
    if (!prefillText) return;
    setText(prefillText);
    setCursor(prefillText.length);
    if (textareaRef.current) {
      textareaRef.current.focus();
      try {
        textareaRef.current.setSelectionRange(
          prefillText.length,
          prefillText.length
        );
      } catch {
        /* ignore */
      }
    }
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillText, onPrefillConsumed]);

  const isPrivate = useMemo(() => {
    if (PRIVATE_PREFIX_RE.test(text)) return true;
    if (SIMPLE_MENTION_RE.test(text)) return true;
    return false;
  }, [text]);

  const insertMention = (user) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(cursor);
    const insertion = `@${user.display_name} `;
    const next = before + insertion + after;
    const newCursor = mention.start + insertion.length;
    setText(next);
    setCursor(newCursor);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        try {
          textareaRef.current.setSelectionRange(newCursor, newCursor);
        } catch (_) {
          /* noop */
        }
      }
    });
  };

  // ─────────────── Phase 7c — file ingest + upload pipeline ───────────────
  const cleanupAttachmentPreviews = useCallback(() => {
    attachments.forEach((a) => {
      if (a.preview_url && a.preview_url.startsWith("blob:")) {
        URL.revokeObjectURL(a.preview_url);
      }
    });
  }, [attachments]);

  // Revoke object URLs on unmount.
  useEffect(() => {
    return () => cleanupAttachmentPreviews();
  }, [cleanupAttachmentPreviews]);

  const uploadOne = useCallback(
    async (localId, file) => {
      const fd = new FormData();
      fd.append("files", file);
      try {
        const { data } = await api.post(
          `/workspaces/${workspaceId}/attachments`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
              if (!e.total) return;
              const pct = Math.round((e.loaded / e.total) * 100);
              setAttachments((prev) =>
                prev.map((a) =>
                  a.local_id === localId ? { ...a, progress: pct } : a
                )
              );
            },
          }
        );
        const server = Array.isArray(data) ? data[0] : data;
        if (!server || !server.id) {
          throw new Error("upload returned no id");
        }
        setAttachments((prev) =>
          prev.map((a) =>
            a.local_id === localId
              ? {
                  ...a,
                  status: "ready",
                  progress: 100,
                  server_id: server.id,
                  // Keep local blob URL for instant preview (cheaper than fetching the
                  // raw endpoint immediately).
                }
              : a
          )
        );
      } catch (e) {
        setAttachments((prev) =>
          prev.map((a) =>
            a.local_id === localId
              ? { ...a, status: "error", error: formatApiError(e) }
              : a
          )
        );
      }
    },
    [workspaceId]
  );

  const acceptFiles = useCallback(
    (incoming) => {
      if (!workspaceId) return;
      const arr = Array.from(incoming || []).filter(Boolean);
      if (!arr.length) return;

      // Local validate + queue + upload.
      setAttachments((prev) => {
        const remainingSlots = MAX_ATTACHMENTS_PER_MESSAGE - prev.length;
        if (remainingSlots <= 0) {
          toast.error(`Max ${MAX_ATTACHMENTS_PER_MESSAGE} images per message`);
          return prev;
        }
        const next = [...prev];
        for (const file of arr.slice(0, remainingSlots)) {
          const localId = newLocalId();
          const type = (file.type || "").toLowerCase();
          if (!ALLOWED_IMAGE_TYPES.has(type)) {
            next.push({
              local_id: localId,
              name: file.name || "file",
              size: file.size || 0,
              type,
              status: "error",
              error: "Unsupported file type (PNG, JPEG, WEBP, GIF only)",
              progress: 0,
              preview_url: null,
            });
            continue;
          }
          if (file.size > MAX_ATTACHMENT_BYTES) {
            next.push({
              local_id: localId,
              name: file.name || "file",
              size: file.size,
              type,
              status: "error",
              error: "File exceeds 10 MB limit",
              progress: 0,
              preview_url: null,
            });
            continue;
          }
          const previewUrl = URL.createObjectURL(file);
          next.push({
            local_id: localId,
            name: file.name || "image",
            size: file.size,
            type,
            status: "uploading",
            progress: 0,
            error: null,
            preview_url: previewUrl,
            file,
          });
          // Kick off upload in a microtask so the optimistic thumbnail renders.
          queueMicrotask(() => uploadOne(localId, file));
        }
        return next;
      });
    },
    [workspaceId, uploadOne]
  );

  const removeAttachment = (localId) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.local_id === localId);
      if (target?.preview_url && target.preview_url.startsWith("blob:")) {
        URL.revokeObjectURL(target.preview_url);
      }
      return prev.filter((a) => a.local_id !== localId);
    });
  };

  const onFileInputChange = (e) => {
    acceptFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  // ─────────────── Drag and drop on the whole composer area ───────────────
  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCountRef.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = () => {
    dragCountRef.current = Math.max(0, dragCountRef.current - 1);
    if (dragCountRef.current === 0) setIsDragging(false);
  };
  const onDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    dragCountRef.current = 0;
    setIsDragging(false);
    acceptFiles(e.dataTransfer.files);
  };

  // ─────────────── Paste handler ───────────────
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      acceptFiles(files);
    }
  };

  // ─────────────── Send pipeline ───────────────
  const anyUploading = attachments.some((a) => a.status === "uploading");
  const readyAttachments = attachments.filter((a) => a.status === "ready");
  // Phase 8 — trial-exhausted block. Only blocks NON-private messages
  // (private/@-mention messages don't call Claude, so they're always free).
  const blockedByTrial = !!trialExhausted && !isPrivate;
  const canSend =
    !sending &&
    !anyUploading &&
    !blockedByTrial &&
    (text.trim().length > 0 || readyAttachments.length > 0);

  const handleSubmit = () => {
    if (!canSend) return;
    const content = text.trim() ? text : "";
    const att_ids = readyAttachments.map((a) => a.server_id);
    const previews = readyAttachments.map((a) => ({
      id: a.server_id,
      filename: a.name,
      content_type: a.type,
      size_bytes: a.size,
      preview_url: a.preview_url,
    }));

    setText("");
    setCursor(0);
    setAttachments([]); // ChatPane keeps the previews via the payload we pass

    try {
      const result = onSend(content, {
        attachment_ids: att_ids,
        optimistic_attachments: previews,
      });
      if (result && typeof result.catch === "function") {
        result.catch(() => {});
      }
    } catch (_) {
      /* noop — ChatPane handles errors via the optimistic bubble. */
    }
  };

  const handleKeyDown = (e) => {
    if (showPicker) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPickerIndex((i) => (i + 1) % filteredUsers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPickerIndex(
          (i) => (i - 1 + filteredUsers.length) % filteredUsers.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredUsers[pickerIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        if (mention) setDismissedAt(mention.start);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);
    setCursor(e.target.selectionStart ?? e.target.value.length);
  };

  const handleSelectOrClick = (e) => {
    if (e.target && typeof e.target.selectionStart === "number") {
      setCursor(e.target.selectionStart);
    }
  };

  const placeholder = (() => {
    const others = candidateUsers.map((u) => u.display_name).filter(Boolean);
    if (others.length === 0) return "Message Claude Opus 4.8…";
    return `Message ${others.join(", ")} and Claude Opus 4.8…`;
  })();

  return (
    <div
      data-testid="composer-area"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="relative"
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={onFileInputChange}
        data-testid="composer-file-input"
      />

      {isDragging ? (
        <div
          className="absolute inset-0 z-30 pointer-events-none rounded-2xl border-2 border-dashed border-[#D96C4A]/70 bg-[#FAF5EA]/85 flex items-center justify-center"
          data-testid="composer-drop-overlay"
        >
          <span className="font-sans text-sm font-medium text-[#7A4A2C]">
            Drop image(s) to attach
          </span>
        </div>
      ) : null}

      <div className="max-w-3xl mx-auto w-full">
        {/* Phase 8 — trial-exhausted block. Visible only when this workspace
            has burned its free trial AND the user is composing a non-private
            message (private/@-mention still goes through because it never
            calls Claude). */}
        {blockedByTrial ? (
          <div
            className="mb-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F8D6CE] border border-[#A8351F]/55 text-[#5E1812] font-sans text-xs"
            data-testid="composer-trial-exhausted"
          >
            <Lock size={12} strokeWidth={2.2} />
            <span>
              Trial exhausted — Claude is paused. Use{" "}
              <code className="px-1 py-0.5 rounded bg-white/50 font-medium">
                /private
              </code>{" "}
              or{" "}
              <code className="px-1 py-0.5 rounded bg-white/50 font-medium">
                @name
              </code>{" "}
              to talk to your teammates, or upgrade to continue using Claude.
            </span>
          </div>
        ) : null}

        {isPrivate ? (
          <div
            className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0EAD9] border border-dashed border-[#7A4A2C]/45 text-[#7A4A2C] w-fit"
            data-testid="composer-private-indicator"
          >
            <Lock size={11} strokeWidth={2.2} />
            <span className="font-sans text-[11px] uppercase tracking-widest font-semibold">
              Private — Claude can&apos;t see this
            </span>
          </div>
        ) : null}

        {/* Attachment strip */}
        {attachments.length > 0 ? (
          <div
            className="mb-2 flex flex-wrap gap-2"
            data-testid="composer-attachments"
          >
            {attachments.map((a) => (
              <div
                key={a.local_id}
                data-testid={`composer-attachment-${a.local_id}`}
                data-status={a.status}
                className={`relative w-20 h-20 rounded-lg overflow-hidden border ${
                  a.status === "error"
                    ? "border-red-400"
                    : "border-[#DCD7CE]"
                } bg-[#FBF8F2] group`}
              >
                {a.preview_url ? (
                  <img
                    src={a.preview_url}
                    alt={a.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-sans text-[10px] text-[#A5A29E] text-center px-1">
                    {a.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(a.local_id)}
                  data-testid={`composer-attachment-remove-${a.local_id}`}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0E0D0C]/70 hover:bg-[#0E0D0C] text-white flex items-center justify-center"
                  aria-label="Remove"
                >
                  <X size={11} strokeWidth={2.2} />
                </button>
                {a.status === "uploading" ? (
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-[#0E0D0C]/30">
                    <div
                      className="h-full bg-[#D96C4A] transition-all"
                      style={{ width: `${a.progress}%` }}
                    />
                  </div>
                ) : null}
                {a.status === "error" ? (
                  <div
                    className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 bg-red-500/85 text-white text-[9px] font-sans font-medium truncate"
                    data-testid={`composer-attachment-error-${a.local_id}`}
                    title={a.error || "Upload failed"}
                  >
                    {a.error || "Failed"}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div
          className={`relative flex items-end w-full rounded-2xl shadow-sm transition-all
            ${
              isPrivate
                ? "bg-[#FAF5EA] border border-dashed border-[#7A4A2C]/40 focus-within:border-[#7A4A2C]/70"
                : "bg-[#F3F1EC] border border-[#DCD7CE] focus-within:ring-2 focus-within:ring-[#D96C4A]/30 focus-within:border-[#D96C4A]/40"
            }`}
        >
          {showPicker ? (
            <div
              className="absolute bottom-full left-3 mb-2 min-w-[240px] max-w-[320px] bg-white rounded-xl shadow-xl border border-[#DCD7CE] overflow-hidden z-20"
              data-testid="mention-picker"
              role="listbox"
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className="px-4 py-2 border-b border-[#EFEAE0] font-sans text-[10px] uppercase tracking-widest text-[#7A7571]">
                Mention a human · marks as private
              </div>
              {filteredUsers.map((u, idx) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => insertMention(u)}
                  onMouseEnter={() => setPickerIndex(idx)}
                  className={`flex w-full items-center justify-between gap-3 text-left px-4 py-2.5 font-sans transition-colors ${
                    idx === pickerIndex
                      ? "bg-[#F3F1EC] text-[#2C2A29]"
                      : "text-[#2C2A29] hover:bg-[#F8F5EE]"
                  }`}
                  data-testid={`mention-picker-item-${u.display_name}`}
                  role="option"
                  aria-selected={idx === pickerIndex}
                >
                  <span className="font-medium text-sm">
                    @{u.display_name}
                  </span>
                  <span className="text-[#7A7571] text-xs truncate">
                    {u.email}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="composer-attach-button"
            disabled={!workspaceId}
            aria-label="Attach image"
            className="absolute left-2 bottom-2 p-2.5 rounded-xl text-[#7A7571] hover:text-[#2C2A29] hover:bg-[#E8E2D2] transition-colors flex items-center justify-center"
            title="Attach image (PNG, JPEG, WEBP, GIF)"
          >
            <Paperclip size={16} strokeWidth={1.8} />
          </button>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={handleSelectOrClick}
            onSelect={handleSelectOrClick}
            onPaste={onPaste}
            placeholder={placeholder}
            rows={1}
            data-testid="composer-textarea"
            className="w-full max-h-[200px] min-h-[56px] resize-none bg-transparent py-4 pl-14 pr-14 focus:outline-none font-sans text-base text-[#2C2A29] placeholder:text-[#A5A29E]"
          />
          <button
            onClick={handleSubmit}
            type="button"
            disabled={!canSend}
            data-testid="composer-send-button"
            data-attachments-pending={anyUploading ? "true" : "false"}
            className={`absolute right-2 bottom-2 p-2.5 rounded-xl text-white disabled:bg-[#A5A29E] disabled:cursor-not-allowed transition-colors flex items-center justify-center
              ${isPrivate ? "bg-[#7A4A2C] hover:bg-[#5E3919]" : "bg-[#2C2A29] hover:bg-[#1A1918]"}`}
            aria-label="Send message"
          >
            {anyUploading ? (
              <Loader2 size={16} strokeWidth={2} className="animate-spin" />
            ) : (
              <Send size={16} strokeWidth={2} />
            )}
          </button>
        </div>

        <div className="mt-2 flex items-center justify-center gap-2 font-sans text-[11px] text-[#A5A29E]">
          <span>
            Enter to send · Shift + Enter for a new line · Use{" "}
            <span className="font-medium text-[#7A4A2C]">@name</span> or{" "}
            <span className="font-medium text-[#7A4A2C]">/private</span> to
            hide from Claude · paste / drop / paperclip to add images
          </span>
          {sending ? (
            <span
              className="font-medium text-[#7A4A2C]"
              data-testid="composer-sending-hint"
            >
              · sending…
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
